import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  IndianRupee, ShoppingBag, Receipt, Users, AlertTriangle, Package, Camera,
  Coins, ArrowUpRight, Clock, Boxes, FileWarning, TrendingUp,
} from "lucide-react";
import api from "@/lib/api";
import { fmtDate, fmtDateTime, inr, STATUS_COLORS } from "@/lib/format";

const TINTS = {
  brand: "bg-brand/10 text-brand",
  emerald: "bg-emerald-100 text-emerald-700",
  violet: "bg-violet-100 text-violet-700",
  amber: "bg-amber-100 text-amber-700",
  sky: "bg-sky-100 text-sky-700",
};

function KpiCard({ icon: Icon, label, value, sub, tint = "brand", to, alert }) {
  const body = (
    <div
      className={`group relative overflow-hidden rounded-2xl border bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${
        alert ? "border-brand/40" : "border-neutral-200/70"
      }`}
    >
      <div className="flex items-start justify-between">
        <span className={`grid h-10 w-10 place-items-center rounded-xl ${TINTS[tint]}`}>
          <Icon size={18} strokeWidth={2} />
        </span>
        {to && <ArrowUpRight size={16} className="text-neutral-300 transition-colors group-hover:text-brand" />}
      </div>
      <p className="mt-4 text-[11px] font-medium uppercase tracking-widest text-neutral-400">{label}</p>
      <p className={`mt-1 font-heading text-[26px] font-bold leading-none ${alert ? "text-brand" : "text-neutral-900"}`}>{value}</p>
      {sub && <p className="mt-2 text-xs text-neutral-500">{sub}</p>}
    </div>
  );
  return to ? <Link to={to} data-testid={`kpi-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>{body}</Link> : body;
}

function SectionCard({ title, action, children, className = "", testid }) {
  return (
    <div className={`rounded-2xl border border-neutral-200/70 bg-white shadow-sm ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-4">
        <h2 className="font-heading text-sm font-semibold tracking-tight text-neutral-900">{title}</h2>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

const ATTENTION = [
  { key: "pending_count", label: "Orders awaiting fulfilment", to: "/admin/orders", icon: Clock, tint: "amber" },
  { key: "low_stock_count", label: "Low / out-of-stock variants", to: "/admin/inventory", icon: Boxes, tint: "brand" },
  { key: "awaiting_photography", label: "Live products awaiting photos", to: "/admin/media", icon: Camera, tint: "violet" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [exposure, setExposure] = useState(null);
  const [notes, setNotes] = useState([]);
  const [metric, setMetric] = useState("revenue");

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/procurement").then((r) => setExposure(r.data.summary)).catch(() => {});
    api.get("/admin/settings").then((r) => setNotes(r.data?.admin_notes || [])).catch(() => {});
  }, []);

  const topMax = useMemo(() => Math.max(1, ...((data?.top_skus || []).map((s) => s.qty))), [data]);
  const blockedDrafts = data?.blocked_drafts || [];

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center" data-testid="dashboard-loading">
        <span className="animate-pulse font-mono text-sm text-neutral-400">LOADING…</span>
      </div>
    );

  const rate = data.rate || {};

  const kpis = [
    { icon: ShoppingBag, label: "Today's Orders", value: data.today_orders, sub: `${data.month_orders} this month`, tint: "brand" },
    { icon: IndianRupee, label: "Today's Revenue", value: inr(data.today_revenue), sub: `${inr(data.month_revenue)} this month`, tint: "emerald" },
    { icon: Receipt, label: "Avg Order Value", value: inr(data.aov), sub: `${data.total_orders} lifetime orders`, tint: "violet" },
    { icon: Users, label: "Customers", value: data.customers, sub: `${inr(data.total_revenue)} lifetime revenue`, tint: "sky", to: "/admin/customers" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-neutral-900">Dashboard</h1>
          <p className="mt-1 text-sm text-neutral-500">A live pulse of your store, orders and metal exposure.</p>
        </div>
        <Link
          to="/admin/rates"
          className="group flex items-center gap-4 self-start rounded-2xl border border-neutral-200/70 bg-white px-4 py-3 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md sm:self-auto"
          data-testid="dashboard-rate-chip"
        >
          <span className={`grid h-9 w-9 place-items-center rounded-xl ${rate.stale ? "bg-amber-100 text-amber-700" : "bg-gold/15 text-gold"}`}>
            <Coins size={17} />
          </span>
          <div className="text-sm">
            <div className="flex items-center gap-3 font-heading font-semibold text-neutral-900">
              <span>24K {rate.gold_24k ? inr(rate.gold_24k) : "—"}</span>
              <span className="text-neutral-300">·</span>
              <span>Ag {rate.silver ? inr(rate.silver) : "—"}</span>
            </div>
            <p className="text-[11px] text-neutral-400">
              {rate.stale ? <span className="font-medium text-amber-600">Rate stale — update now</span> : `Updated ${rate.updated_at ? fmtDateTime(rate.updated_at) : "—"}`}
            </p>
          </div>
        </Link>
      </div>

      {/* Alerts */}
      {exposure?.alert && (
        <Link to="/admin/operations" className="block rounded-2xl border border-red-200 bg-red-50/80 p-4 transition-colors hover:bg-red-50" data-testid="rate-exposure-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 shrink-0 text-red-500" size={18} />
            <div>
              <p className="font-heading font-semibold text-red-700">Rate exposure — unhedged made-to-order metal</p>
              <p className="mt-1 text-sm text-red-700">
                {exposure.pending} paid order line(s) awaiting procurement · {exposure.unhedged_fine_grams}g fine metal to buy · current exposure {inr(exposure.unhedged_exposure_inr)}
                {exposure.stale_count ? ` · ${exposure.stale_count} older than ${exposure.alert_hours}h` : ""}
                {exposure.moved_count ? ` · ${exposure.moved_count} past ±${exposure.alert_move_pct}% rate move` : ""}
              </p>
              <p className="mt-1 font-mono text-xs text-red-600">Open Operations → Procurement and buy today's metal →</p>
            </div>
          </div>
        </Link>
      )}

      {notes.filter((n) => n.status !== "resolved").map((n) => (
        <div key={n.id} className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4" data-testid={`admin-note-${n.id}`}>
          <p className="font-heading font-semibold text-amber-800">{n.title}</p>
          <p className="mt-1 text-sm leading-relaxed text-amber-900">{n.body}</p>
          <p className="mt-1 font-mono text-xs text-amber-700">Flagged {n.added} · status: {n.status} — not built, not on the storefront.</p>
        </div>
      ))}

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {kpis.map((k) => <KpiCard key={k.label} {...k} />)}
      </div>

      {/* Revenue + Needs attention */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <SectionCard
          testid="revenue-chart-card"
          className="lg:col-span-2"
          title="Performance — last 7 days"
          action={
            <div className="flex rounded-lg bg-neutral-100 p-0.5" data-testid="revenue-metric-toggle">
              {["revenue", "orders"].map((m) => (
                <button
                  key={m}
                  onClick={() => setMetric(m)}
                  className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors ${metric === m ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500 hover:text-neutral-800"}`}
                  data-testid={`metric-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
          }
        >
          <div className="mb-4 flex items-baseline gap-2">
            <TrendingUp size={16} className="text-emerald-500" />
            <span className="font-heading text-xl font-bold text-neutral-900">
              {metric === "revenue" ? inr(data.total_revenue) : data.total_orders}
            </span>
            <span className="text-xs text-neutral-400">total {metric}</span>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.revenue_series} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgb(var(--brand-rgb))" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="rgb(var(--brand-rgb))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => fmtDate(d).replace(/,.*/, "")} tick={{ fontSize: 11, fill: "#a3a3a3" }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 11, fill: "#a3a3a3" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => (metric === "revenue" ? `₹${(v / 1000).toFixed(0)}k` : v)}
                />
                <Tooltip
                  formatter={(v) => [metric === "revenue" ? inr(v) : v, metric]}
                  labelFormatter={(d) => fmtDate(d)}
                  contentStyle={{ borderRadius: 12, border: "1px solid #eee", fontSize: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}
                />
                <Area type="monotone" dataKey={metric} stroke="rgb(var(--brand-rgb))" fill="url(#revGrad)" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Needs attention" testid="needs-attention-card">
          <ul className="space-y-2">
            {ATTENTION.map(({ key, label, to, icon: Icon, tint }) => {
              const count = data[key] || 0;
              return (
                <li key={key}>
                  <Link
                    to={to}
                    className="flex items-center gap-3 rounded-xl border border-neutral-100 px-3 py-2.5 transition-colors hover:border-neutral-200 hover:bg-neutral-50"
                    data-testid={`attention-${key}`}
                  >
                    <span className={`grid h-8 w-8 place-items-center rounded-lg ${count ? TINTS[tint] : "bg-neutral-100 text-neutral-400"}`}>
                      <Icon size={15} />
                    </span>
                    <span className="flex-1 text-sm text-neutral-700">{label}</span>
                    <span className={`font-heading text-lg font-bold ${count ? "text-neutral-900" : "text-neutral-300"}`}>{count}</span>
                  </Link>
                </li>
              );
            })}
            {blockedDrafts.length > 0 && (
              <li>
                <Link to="/admin/products?status=draft" className="flex items-center gap-3 rounded-xl border border-neutral-100 px-3 py-2.5 transition-colors hover:border-neutral-200 hover:bg-neutral-50" data-testid="attention-blocked-drafts">
                  <span className="grid h-8 w-8 place-items-center rounded-lg bg-red-100 text-red-600"><FileWarning size={15} /></span>
                  <span className="flex-1 text-sm text-neutral-700">Drafts blocked (missing weight)</span>
                  <span className="font-heading text-lg font-bold text-neutral-900">{blockedDrafts.length}</span>
                </Link>
              </li>
            )}
          </ul>
        </SectionCard>
      </div>

      {/* Pending queue + Top sellers */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard
          title={`Fulfilment queue${data.pending_count ? ` · ${data.pending_count}` : ""}`}
          testid="pending-orders-card"
          action={<Link to="/admin/orders" className="text-xs font-medium text-brand" data-testid="goto-orders-pending">View all →</Link>}
        >
          {(data.pending_orders || []).length === 0 ? (
            <p className="py-6 text-center text-sm text-neutral-400">Nothing waiting. All caught up.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.pending_orders.map((o) => (
                <li key={o.id}>
                  <Link to={`/admin/orders/${o.id}`} className="flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-neutral-50" data-testid={`pending-order-${o.order_number}`}>
                    <span className="grid h-9 w-9 place-items-center rounded-lg bg-amber-50 text-amber-600"><Package size={15} /></span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-mono text-sm font-medium text-neutral-900">{o.order_number}</p>
                      <p className="truncate text-xs text-neutral-400">{o.customer?.name || "—"} · {fmtDateTime(o.created_at)}</p>
                    </div>
                    <span className="font-heading text-sm font-semibold text-neutral-900">{inr(o.total)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard title="Top sellers this month" testid="top-sellers-card">
          {(data.top_skus || []).length === 0 ? (
            <p className="py-6 text-center text-sm text-neutral-400">No sales recorded yet this month.</p>
          ) : (
            <ul className="space-y-3.5">
              {data.top_skus.map((s, i) => (
                <li key={s.title + i} data-testid={`top-sku-${i}`}>
                  <div className="mb-1 flex items-baseline justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="font-mono text-xs text-neutral-300">{String(i + 1).padStart(2, "0")}</span>
                      <span className="truncate text-sm text-neutral-800">{s.title}</span>
                    </span>
                    <span className="shrink-0 text-xs text-neutral-500">{s.qty} sold · {inr(s.revenue)}</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-neutral-100">
                    <div className="h-full rounded-full bg-gradient-to-r from-brand/70 to-brand transition-all duration-500" style={{ width: `${(s.qty / topMax) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      {/* Low stock + Recent orders */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <SectionCard
          title="Low stock (≤ 5)"
          testid="low-stock-card"
          action={<Link to="/admin/inventory" className="text-xs font-medium text-brand" data-testid="goto-inventory">Manage →</Link>}
        >
          {data.low_stock.length === 0 ? (
            <p className="py-6 text-center text-sm text-neutral-400">All stocked up.</p>
          ) : (
            <ul className="max-h-64 space-y-2 overflow-y-auto pr-1">
              {data.low_stock.map((s) => (
                <li key={s.variant_id} className="flex items-center justify-between gap-2 border-b border-neutral-100 pb-2 last:border-0">
                  <span className="min-w-0 truncate text-sm text-neutral-700">
                    {s.title} <span className="font-mono text-xs text-neutral-400">{s.sku}</span>
                  </span>
                  <span className={`shrink-0 rounded-md px-2 py-0.5 font-mono text-xs font-semibold ${s.stock === 0 ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{s.stock}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard
          title="Recent orders"
          testid="recent-orders-card"
          className="lg:col-span-2"
          action={<Link to="/admin/orders" className="text-xs font-medium text-brand" data-testid="goto-orders">View all →</Link>}
        >
          {data.recent_orders.length === 0 ? (
            <p className="py-6 text-center text-sm text-neutral-400">No orders yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-medium uppercase tracking-widest text-neutral-400">
                    <th className="pb-3">Order</th>
                    <th className="pb-3">Customer</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_orders.map((o) => (
                    <tr key={o.id} className="border-t border-neutral-100 transition-colors hover:bg-neutral-50" data-testid={`recent-order-${o.order_number}`}>
                      <td className="py-3">
                        <Link to={`/admin/orders/${o.id}`} className="font-mono font-medium text-neutral-900 hover:text-brand">{o.order_number}</Link>
                        <p className="text-xs text-neutral-400">{fmtDateTime(o.created_at)}</p>
                      </td>
                      <td className="text-neutral-700">{o.customer?.name}</td>
                      <td><span className={`rounded-md px-2 py-0.5 text-[11px] font-medium uppercase ${STATUS_COLORS[o.status] || "bg-neutral-100"}`}>{o.status}</span></td>
                      <td className="text-right font-semibold text-neutral-900">{inr(o.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
